// src/pages/HelpPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie, faUser, faExclamationCircle, faThList,
    faPlus, faUserPlus, faEdit, faUserEdit, faTrashAlt, faFilePdf,
    faSitemap, faRandom, faMobileAlt, faSpinner, faLifeRing, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Container, Row, Col, Card, Spinner, Alert, Breadcrumb, Button } from 'react-bootstrap';

function HelpPage() {
    const { currentUser, isAdmin, loadingAuth } = useAuth();

    if (loadingAuth) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading help content...</span>
                </Spinner>
                <p className="mt-2">Loading help center...</p>
            </Container>
        );
    }

    return (
        <Container className="mt-3">
            <Row>
                <Col>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item active>Help</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>

            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <div className="mb-3">
                        <h1>
                            <FontAwesomeIcon icon={faLifeRing} className="me-2" />
                            Help Center
                        </h1>
                        <p className="lead">Find answers and guidance on using the application.</p>
                    </div>

                    {isAdmin && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Body>
                                <Card.Title as="h3"><FontAwesomeIcon icon={faUserTie} className="me-2" />Administrators</Card.Title>
                                <Card.Text>Create an unlimited number of admin accounts to manage your account. Administrator accounts can view, edit, and maintain all information.</Card.Text>
                                <h5>Upgrade a user to Admin</h5>
                                <Card.Text>Click the ‘Make Admin’ link beside the username on the <Link to="/admin/users">Users page</Link>.</Card.Text>
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="mb-3 shadow-sm">
                        <Card.Body>
                            <Card.Title as="h3"><FontAwesomeIcon icon={faUser} className="me-2" />User Accounts</Card.Title>
                            <Card.Text>Members in your directory can create a free user account to view your directory, and edit the information for their family.</Card.Text>
                            <Card.Text>
                                <FontAwesomeIcon icon={faExclamationCircle} style={{ color: 'red' }} className="me-1" />
                                Only members with a valid email address in your directory can create an account and view your directory.
                            </Card.Text>
                            <h5>Create Member Login</h5>
                            <Card.Text>Church members are granted access to your directory by <Link to="/create-account">Creating A User Account</Link> with the same email address as their member email address in your directory.</Card.Text>
                        </Card.Body>
                    </Card>

                    {/* Member Directory Section - UPDATED */}
                    <Card className="mb-3 shadow-sm">
                        <Card.Body>
                            <Card.Title as="h3"><FontAwesomeIcon icon={faThList} className="me-2" />Member Directory</Card.Title>

                            {/* Admin specific general message */}
                            {isAdmin && (
                                <Alert variant="info" className="mt-2 small">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    As an administrator, you have full permissions to add new families, and to view, edit, and delete all existing family and member details in the directory.
                                </Alert>
                            )}

                            {/* General description based on role */}
                            {isAdmin ? (
                                <Card.Text>Add families, then add members to specific families. Directory information syncs to the mobile directory and downloadable pdf directory.</Card.Text>
                            ) : (
                                <Card.Text>View church member information. You can update information for your own family and the members within it.</Card.Text>
                            )}

                            {/* Admin-only: Adding families and members */}
                            {isAdmin && (
                                <>
                                    <h5>Add a family <Button variant="success" size="sm" disabled><FontAwesomeIcon icon={faPlus} /> Add Family</Button></h5>
                                    <Card.Text>Create a new family using the Add Family button on the <Link to="/families">Families page</Link>.</Card.Text>

                                    <h5>Add a member <Button variant="primary" size="sm" disabled><FontAwesomeIcon icon={faUserPlus} /></Button></h5>
                                    <Card.Text>New members can be added to any family, either on the <Link to="/families">Families page</Link> (by finding the family first) or directly on a specific Family's Page.</Card.Text>
                                </>
                            )}

                            <h5>View Specific Family</h5>
                            <Card.Text>Click the family name on the <Link to="/families">Families page</Link>, or family picture on the <Link to="/families/grid">Families Grid page</Link>, to view a specific family.</Card.Text>

                            <h5>Edit Family <FontAwesomeIcon icon={faEdit} style={{ color: 'blue' }} /></h5>
                            <Card.Text>
                                Click the yellow pencil icon button <FontAwesomeIcon icon={faEdit} style={{ color: 'blue' }} /> on the Family Page to modify family details (e.g., surname, address, photo).
                                {!isAdmin && (
                                    <span className="d-block fst-italic small mt-1">
                                        Note: As a non-administrator, you can only edit your own family's information.
                                    </span>
                                )}
                            </Card.Text>

                            <h5>Edit Member <FontAwesomeIcon icon={faUserEdit} style={{ color: 'blue' }} /></h5>
                            <Card.Text>
                                Click the edit icon <FontAwesomeIcon icon={faUserEdit} style={{ color: 'blue' }} /> beside a member's name on the <Link to="/families">Families page</Link> or on a Family Page to update their individual information.
                                {!isAdmin && (
                                    <span className="d-block fst-italic small mt-1">
                                        Note: As a non-administrator, you can only edit members within your own family.
                                    </span>
                                )}
                            </Card.Text>

                            {/* Admin-only: Deleting actions */}
                            {isAdmin && (
                                <>
                                    <h5>Delete Family <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'red' }} /></h5>
                                    <Card.Text>Click the trash icon Red Delete Family button on the family page.</Card.Text>

                                    <h5>Delete Member <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'blue' }} /></h5>
                                    <Card.Text>Click the trash icon beside the member to delete on the <Link to="/families">Families page</Link> or on a Family Page.</Card.Text>
                                </>
                            )}


                            {/* Admin-only: PDF Download */}
                            {isAdmin && (
                                <>
                                    <hr className="my-3" />
                                    <h5>Download Directory PDF <Button variant="primary" size="sm" disabled><FontAwesomeIcon icon={faFilePdf} /> Download PDF</Button></h5>
                                    <Card.Text>Click the ‘Download PDF’ button on the <Link to="/families">Families page</Link>, wait for the directory file to finish updating, and it will download automatically.</Card.Text>
                                    <Card.Text>Only admin users can download a PDF of the directory.</Card.Text>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Groups Section */}
                    <Card className="mb-3 shadow-sm">
                        <Card.Body>
                            <Card.Title as="h3"><FontAwesomeIcon icon={faSitemap} className="me-2" />Groups</Card.Title>
                            <Card.Text>View members grouped by role to make it easy to find the right contact.</Card.Text>
                            {isAdmin && (
                                <>
                                    <h5>Create a group <Button variant="success" size="sm" disabled><FontAwesomeIcon icon={faPlus} /> Create Group</Button></h5>
                                    <Card.Text>Click the ‘Create Group’ button on the <Link to="/groups">Group page</Link> and enter a group name.</Card.Text>

                                    <h5>Delete a group <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'red' }} /></h5>
                                    <Card.Text>Click the trash icon at the bottom right corner of the group on the <Link to="/groups">Group page</Link>.</Card.Text>

                                    <h5>Add members to a group <Button variant="primary" size="sm" disabled><FontAwesomeIcon icon={faUserPlus} /></Button></h5>
                                    <Card.Text>Click the person icon at the bottom right corner of the group, and select a member to add.</Card.Text>

                                    <h5>Remove from group <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'blue' }} /></h5>
                                    <Card.Text>Click the trash icon under the members name on the <Link to="/groups">Group page</Link>, or the red x icon on the member on the Specific Family Page.</Card.Text>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Mobile App Section */}
                    <Card className="mb-3 shadow-sm">
                        <Card.Body>
                            <Card.Title as="h3"><FontAwesomeIcon icon={faMobileAlt} className="me-2" />Mobile App</Card.Title>
                            <Card.Text>Member information automatically syncs to your iPhone or Android smart phone with the Church Aware app.</Card.Text>
                            <h5>Download the app</h5>
                            <Card.Text>Search your app store for 'Church Aware'.</Card.Text>
                            <Card.Text>Login with the same username and password as the Church Aware website.</Card.Text>
                            <h5>Mobile App Features</h5>
                            <Card.Text>View a listing of your members by mobile cell phone number, family, or groups.</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default HelpPage;